<template>
  <div class="bg-gray-50 min-h-screen">
    <div class="container mx-auto px-6 py-12">
      <div class="text-center mb-12">
        <h1 class="text-4xl font-extrabold text-gray-800">Legal & Privacy Center</h1>
        <p class="mt-2 text-lg text-gray-600">Our commitment to transparency and your data privacy.</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <!-- Left Column: Sticky Navigation -->
        <aside class="lg:col-span-1">
          <div class="sticky top-24">
            <h3 class="text-lg font-semibold text-gray-700 mb-4">On this page</h3>
            <ul class="space-y-2">
              <li><a href="#terms" @click.prevent="scrollToSection('terms')" class="text-gray-600 hover:text-blue-600 font-medium">Terms of Service</a></li>
              <li><a href="#privacy" @click.prevent="scrollToSection('privacy')" class="text-gray-600 hover:text-blue-600 font-medium">Privacy Policy</a></li>
            </ul>
          </div>
        </aside>

        <!-- Right Column: Main Content -->
        <main class="lg:col-span-3 bg-white p-8 rounded-lg shadow-md">
          <!-- Terms of Service -->
          <section id="terms" class="prose max-w-none mb-16">
            <h2 class="text-3xl font-bold">Terms of Service</h2>
            <p class="text-sm text-gray-500"><strong>Effective Date:</strong> 28 September 2024</p>

            <h3>1. Services Provided</h3>
            <p>
              Easymarks.ai offers a service that allows users to upload or submit images of student papers, which are processed using AI technology to provide feedback and analysis. The results provided are based on AI models, including those provided by <strong>Google Gemini</strong>, and while we strive for accuracy, you acknowledge that errors may occur, and Easymarks.ai is not responsible for any consequences resulting from such errors.
            </p>

            <h3>2. Use of the Website</h3>
            <p>By using this Website, you agree to:</p>
            <ul>
              <li>Use the Website for lawful purposes only.</li>
              <li>Not upload or transmit any content that infringes the rights of others, including intellectual property rights.</li>
              <li>Not use any automated system or software to extract data or interfere with the Website's functionality.</li>
            </ul>
            <p>Easymarks.ai reserves the right to terminate your access to the Website for any violation of these Terms.</p>

            <h3>3. Disclaimer of Warranties</h3>
            <p>
              The Website and the services provided are on an "as-is" and "as available" basis. Easymarks.ai makes no representations or warranties about the accuracy, completeness, reliability, suitability, or availability of the services.
            </p>

            <h3>4. Limitation of Liability</h3>
            <p>
              In no event shall Easymarks.ai, its affiliates, or its service providers be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of or connected to your use of the Website.
            </p>

            <h3>5. Privacy</h3>
            <p>
              By using Easymarks.ai, you agree to our Privacy Policy, which explains how we collect, use, and protect your information. Easymarks.ai does not train its AI models on your data and will not share any personal information with third parties, except for processing purposes with <strong>Google</strong>.
            </p>

            <h3>6. Intellectual Property</h3>
            <p>
              All intellectual property rights related to Easymarks.ai, including the software, website design, and content, are owned by or licensed to Easymarks.ai. You agree not to copy, distribute, or modify any part of the Website without prior written consent.
            </p>

            <h3>7. Changes to the Terms</h3>
            <p>We may update these Terms from time to time. Please review these Terms periodically to stay informed of any updates.</p>

            <h3>8. Governing Law</h3>
            <p>These Terms shall be governed by the laws of the EU, without regard to its conflict of laws principles.</p>

            <h3>9. Contact Information</h3>
            <p>If you have any questions about these Terms, please contact us at <a href="mailto:support@easymarks.ai">support@easymarks.ai</a>.</p>
          </section>

          <!-- Privacy Policy -->
          <section id="privacy" class="prose max-w-none">
            <h2 class="text-3xl font-bold">Privacy Policy</h2>
            <p class="text-sm text-gray-500"><strong>Effective Date:</strong> 28 September 2024</p>

            <h3>1. Information We Collect</h3>
            <p>We collect the following types of information:</p>
            <ul>
              <li><strong>Personal Information:</strong> When you create an account or interact with Easymarks.ai, we may collect your name, email address, and other information you provide.</li>
              <li><strong>User Content:</strong> When you upload images of student papers, we collect and process the data using AI models.</li>
              <li><strong>Technical Information:</strong> We may collect device information, browser data, IP address, and usage data to improve user experience.</li>
            </ul>

            <h3>2. How We Use Your Information</h3>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide and improve Easymarks.ai services.</li>
              <li>Communicate with you and send updates or information.</li>
              <li>Process data using AI models for analysis purposes.</li>
            </ul>

            <h3>3. Sharing of Data</h3>
            <p>We do not sell, rent, or share your personal data with any third parties, except as required to provide our services through <strong>Google's AI Platform</strong>.</p>

            <h3>4. Data Retention</h3>
            <p>We retain your data only for as long as necessary or as required by law. To delete your data, contact us at <a href="mailto:support@easymarks.ai">support@easymarks.ai</a>.</p>

            <h3>5. Security</h3>
            <p>We take reasonable steps to protect your data but cannot guarantee absolute security due to the nature of internet transmissions.</p>

            <h3>6. Your Rights</h3>
            <p>You have the right to access, correct, or delete your data, and to opt-out of communications. Contact us at <a href="mailto:support@easymarks.ai">support@easymarks.ai</a> for assistance.</p>

            <h3>7. Use of Cookies</h3>
            <p>We use cookies to collect information and improve user experience. You can control cookie use through your browser settings.</p>

            <h3>8. Changes to This Policy</h3>
            <p>We may update this Privacy Policy from time to time. Please review this Policy periodically to stay informed.</p>

            <h3>9. Contact Us</h3>
            <p>If you have any questions or concerns about this Privacy Policy, please contact us at <a href="mailto:support@easymarks.ai">support@easymarks.ai</a>.</p>
          </section>
        </main>
      </div>
    </div>
  </div>
</template>

<script setup>
const scrollToSection = (sectionId) => {
  const section = document.getElementById(sectionId)
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>

<style scoped>
/* To get beautiful typography for the legal text with minimal effort,
   we can use the official Tailwind CSS Typography plugin.
   If you don't have it installed, run: npm install -D @tailwindcss/typography
   Then, add this to your tailwind.config.js file:
   plugins: [
     require('@tailwindcss/typography'),
   ],
*/
.prose h3 {
  @apply mt-6 mb-2 font-semibold;
}
.prose ul {
  @apply list-disc list-inside;
}
</style>
